import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, X } from "lucide-react";
import { packagesApi } from "../api/client.ts";
import { useAuth } from "../lib/auth.tsx";

const FormSchema = z.object({
  name: z.string().min(1).max(80),
  durationMinutes: z.coerce.number().int().positive(),
  priceKes: z.coerce.number().int().nonnegative(),
  sortOrder: z.coerce.number().int().optional(),
});
type FormValues = z.infer<typeof FormSchema>;

export function PackagesPage() {
  const { tenant } = useAuth();
  const auth = { tenantId: tenant!.tenantId, idToken: tenant!.idToken };
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const list = useQuery({
    queryKey: ["packages", auth.tenantId],
    queryFn: () => packagesApi.list(auth),
  });

  const create = useMutation({
    mutationFn: (values: FormValues) => packagesApi.create(auth, values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["packages", auth.tenantId] });
      setShowForm(false);
    },
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      packagesApi.update(auth, id, { active }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["packages", auth.tenantId] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Packages</h2>
          <p className="text-sm text-slate-500">
            Define the WiFi bundles your customers buy via STK Push.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showForm ? "Cancel" : "New package"}
        </button>
      </div>

      {showForm && (
        <CreatePackageForm
          submitting={create.isPending}
          error={create.error?.message}
          onSubmit={(values) => create.mutate(values)}
        />
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Duration</th>
              <th className="px-4 py-3 font-medium">Price (KES)</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {list.isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {list.error && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-red-600">
                  {list.error.message}
                </td>
              </tr>
            )}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No packages yet. Click <strong>New package</strong> to add one.
                </td>
              </tr>
            )}
            {list.data?.map((p) => (
              <tr key={p.id}>
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3 text-slate-600">
                  {formatDuration(p.durationMinutes)}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {p.priceKes.toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={
                      p.active
                        ? "rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700"
                        : "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500"
                    }
                  >
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() =>
                      toggleActive.mutate({ id: p.id, active: !p.active })
                    }
                    disabled={toggleActive.isPending}
                    className="text-xs font-medium text-slate-500 hover:text-slate-900 disabled:opacity-50"
                  >
                    {p.active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreatePackageForm({
  onSubmit,
  submitting,
  error,
}: {
  onSubmit: (v: FormValues) => void;
  submitting: boolean;
  error?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema) });
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="grid gap-4 rounded-xl border border-slate-200 bg-white p-6 sm:grid-cols-4"
    >
      <Field label="Name" error={errors.name?.message}>
        <input
          {...register("name")}
          placeholder="e.g. 24 Hours"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </Field>
      <Field label="Duration (minutes)" error={errors.durationMinutes?.message}>
        <input
          type="number"
          {...register("durationMinutes")}
          placeholder="1440"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </Field>
      <Field label="Price (KES)" error={errors.priceKes?.message}>
        <input
          type="number"
          {...register("priceKes")}
          placeholder="50"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </Field>
      <Field label="Sort order" error={errors.sortOrder?.message}>
        <input
          type="number"
          {...register("sortOrder")}
          placeholder="0"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </Field>
      {error && (
        <p className="col-span-full text-sm text-red-600">{error}</p>
      )}
      <div className="col-span-full">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {submitting ? "Creating…" : "Create package"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <div className="mt-1">{children}</div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </label>
  );
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} min`;
  if (mins < 1440) return `${mins / 60} hr`;
  if (mins < 10080) return `${mins / 1440} days`;
  if (mins < 43200) return `${mins / 10080} weeks`;
  return `${mins / 43200} months`;
}
